import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthState } from '@/hooks/useAuth'
import { collection, addDoc, getDocs, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { AuthModal } from "@/components/auth/AuthModal"
import { CitySelector, type CityData } from '@/components/shared/CitySelector'
import { getAllCities, loadWorldCities, isWorldCitiesLoaded } from '@/lib/worldCities'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

export default function RequestGuestArtistPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthState()
  
  // Get preselected city from navigation state
  const preselectedCityName = (location.state as any)?.selectedCity || ''
  
  const [selectedCityData, setSelectedCityData] = useState<CityData | null>(null)
  const [artistInstagram, setArtistInstagram] = useState<string>('')
  const [isFlash, setIsFlash] = useState<boolean>(true)
  const [customIdea, setCustomIdea] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [existingArtistInstagrams, setExistingArtistInstagrams] = useState<string[]>([])
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([])
  const [confidenceLevel, setConfidenceLevel] = useState<number>(4)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Handle city selection
  const handleCityChange = (cityName: string, cityData: CityData | null) => {
    console.log('[CitySelector] City changed:', cityName, cityData)
    setSelectedCityData(cityData)
  }

  // Load world cities and preselect city if provided
  useEffect(() => {
    const loadAndPreselect = async () => {
      try {
        if (!isWorldCitiesLoaded()) {
          await loadWorldCities()
        }
        
        // If a city was preselected, find and set it
        if (preselectedCityName) {
          const allCities = getAllCities()
          const foundCity = allCities.find(city => city.city === preselectedCityName)
          if (foundCity) {
            console.log('[RequestGuestArtistPage] Preselected city:', foundCity)
            setSelectedCityData(foundCity)
          }
        }
      } catch (error) {
        console.error('[RequestGuestArtistPage] Error loading cities:', error)
      }
    }

    loadAndPreselect()
  }, [preselectedCityName])

  // Fetch existing artist Instagram names from requests for autocomplete
  useEffect(() => {
    const fetchExistingArtistNames = async () => {
      try {
        const requestsRef = collection(db, 'guestSpotRequests')
        const snapshot = await getDocs(requestsRef)
        
        const uniqueInstagrams = new Set<string>()
        snapshot.forEach((doc) => {
          const data = doc.data()
          if (data.artistInstagram) {
            uniqueInstagrams.add(data.artistInstagram)
          }
        })
        
        setExistingArtistInstagrams(Array.from(uniqueInstagrams).sort())
      } catch (error) {
        console.error('Error fetching existing artist names:', error)
      }
    }

    fetchExistingArtistNames()
  }, [])

  // Handle clicking outside suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showSuggestions])

  // Filter suggestions based on input
  const handleArtistInstagramChange = (value: string) => {
    setArtistInstagram(value)
    
    if (value.trim()) {
      const filtered = existingArtistInstagrams.filter((instagram) =>
        instagram.toLowerCase().includes(value.toLowerCase())
      )
      setFilteredSuggestions(filtered)
      setShowSuggestions(filtered.length > 0)
    } else {
      setFilteredSuggestions(existingArtistInstagrams)
      setShowSuggestions(false)
    }
  }

  const handleSuggestionSelect = (suggestion: string) => {
    setArtistInstagram(suggestion)
    setShowSuggestions(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Form submitted, user:', user)
    console.log('Current state - selectedCityData:', selectedCityData)

    if (!user) {
      console.log('No user logged in, showing auth modal')
      setShowAuthModal(true)
      return
    }

    if (!selectedCityData) {
      console.log('No city selected - selectedCityData:', selectedCityData)
      toast.error('Please select a city')
      return
    }

    if (!artistInstagram.trim()) {
      console.log('No artist Instagram entered')
      toast.error('Please enter an artist Instagram name')
      return
    }

    console.log('Validation passed, attempting to submit...')
    setIsSubmitting(true)

    try {
      const requestsRef = collection(db, 'guestSpotRequests')
      
      const docData = {
        userId: user.uid,
        artistInstagram: artistInstagram.trim().replace('@', ''),
        location: {
          city: selectedCityData.city,
          country: selectedCityData.country,
          lat: selectedCityData.latitude,
          lon: selectedCityData.longitude,
        },
        isFlash: isFlash,
        customIdea: !isFlash && customIdea.trim() ? customIdea.trim() : null,
        confidenceLevel: confidenceLevel,
        createdAt: Timestamp.now(),
      }
      
      console.log('Submitting document with data:', docData)
      const docRef = await addDoc(requestsRef, docData)
      console.log('Document successfully added with ID:', docRef.id)

      toast.success('Artist request submitted!')
      // Reset form after successful submission
      setArtistInstagram('')
      setSelectedCityData(null)
      setIsFlash(true)
      setCustomIdea('')
      setConfidenceLevel(4)
      
      navigate('/guest-artists')
    } catch (error) {
      console.error('Error submitting request:', error)
      if (error instanceof Error) {
        console.error('Error message:', error.message)
        console.error('Error stack:', error.stack)
      }
      toast.error('Failed to submit request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate('/guest-artists')
  }

  return (
    <>
      <div className="flex flex-col bg-background min-h-screen-mobile-minus-nav">
        {/* Header */}
        <div className="flex-shrink-0 sticky top-0 z-50 bg-background border-b border-border">
          <div className="phone-container-padded py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-muted rounded-md transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">Request an Artist</h1>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="phone-container-padded py-6 overflow-y-auto flex-1 pb-20">
          <form onSubmit={handleSubmit} className="space-y-6 w-full mx-auto" style={{ minWidth: '280px' }}>
            {/* City Selection */}
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <CitySelector
                value={selectedCityData?.city || ''}
                onChange={handleCityChange}
                placeholder="Choose a city"
                className="w-full"
              />
            </div>

            {/* Artist Instagram */}
            <div className="space-y-2 relative">
              <Label htmlFor="artistInstagram">Artist Instagram Name</Label>
              <div className="relative">
                <Input
                  id="artistInstagram"
                  type="text"
                  placeholder="tattoo_boss_56"
                  value={artistInstagram}
                  onChange={(e) => handleArtistInstagramChange(e.target.value)}
                  onFocus={() => {
                    if (artistInstagram.trim() === '' && existingArtistInstagrams.length > 0) {
                      setFilteredSuggestions(existingArtistInstagrams)
                      setShowSuggestions(true)
                    }
                  }}
                  autoComplete="off"
                  required
                />
                
                {/* Custom Autocomplete Dropdown */}
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div
                    ref={suggestionsRef}
                    className="absolute top-full left-0 right-0 z-50 mt-1 bg-card border border-border rounded-md shadow-lg max-h-48 overflow-y-auto"
                  >
                    {filteredSuggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => handleSuggestionSelect(suggestion)}
                        className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Flash or Custom Switch */}
            <div className="flex items-center justify-between">
              <Label htmlFor="booking-type">Booking Type</Label>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${isFlash ? 'font-medium' : 'text-muted-foreground'}`}>
                  Flash
                </span>
                <Switch
                  id="booking-type"
                  checked={!isFlash}
                  onCheckedChange={(checked) => setIsFlash(!checked)}
                />
                <span className={`text-sm ${!isFlash ? 'font-medium' : 'text-muted-foreground'}`}>
                  Custom
                </span>
              </div>
            </div>

            {/* Custom Idea (optional, only shown if custom) */}
            {!isFlash && (
              <div className="space-y-2">
                <Label htmlFor="customIdea">Custom Idea (Optional)</Label>
                <Textarea
                  id="customIdea"
                  placeholder="Describe your tattoo idea..."
                  value={customIdea}
                  onChange={(e) => setCustomIdea(e.target.value)}
                  rows={4}
                />
              </div>
            )}

            {/* Confidence Level Slider */}
            <div className="space-y-3">
              <Label>
                How sure are you about wanting a tattoo from this artist?
              </Label>
              <div className="space-y-2 pt-2">
                <Slider
                  value={[confidenceLevel]}
                  onValueChange={(value) => setConfidenceLevel(value[0])}
                  min={1}
                  max={7}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                  <span>6</span>
                  <span>7</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>
        </div>
      </div>
      <AuthModal 
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
      />
    </>
  )
}






